import { useMemo, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { python } from '@codemirror/lang-python';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { sql } from '@codemirror/lang-sql';
import { java } from '@codemirror/lang-java';
import { cpp } from '@codemirror/lang-cpp';
import { rust } from '@codemirror/lang-rust';
import { go } from '@codemirror/lang-go';
import { php } from '@codemirror/lang-php';
import { xml } from '@codemirror/lang-xml';
import { yaml } from '@codemirror/lang-yaml';
import { vue } from '@codemirror/lang-vue';
import { sass } from '@codemirror/lang-sass';
import { less } from '@codemirror/lang-less';
import { wast } from '@codemirror/lang-wast';
import { liquid } from '@codemirror/lang-liquid';
import { StreamLanguage } from '@codemirror/language';
import { ruby } from '@codemirror/legacy-modes/mode/ruby';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { dockerFile } from '@codemirror/legacy-modes/mode/dockerfile';
import { csharp, kotlin, scala } from '@codemirror/legacy-modes/mode/clike';
import { swift } from '@codemirror/legacy-modes/mode/swift';
import { perl } from '@codemirror/legacy-modes/mode/perl';
import { lua } from '@codemirror/legacy-modes/mode/lua';
import { r as rLang } from '@codemirror/legacy-modes/mode/r';
import { haskell } from '@codemirror/legacy-modes/mode/haskell';
import { clojure } from '@codemirror/legacy-modes/mode/clojure';
import { powerShell } from '@codemirror/legacy-modes/mode/powershell';
import './Playground.css';

/** @typedef {'web' | 'python' | 'json' | 'none'} RunKind */

const WEB_IDS = new Set(['html', 'css', 'javascript']);

function legacyMode(mode) {
  return () => StreamLanguage.define(mode);
}

const LANG_DEFS = [
  { id: 'html', label: 'HTML', ext: () => html(), runKind: 'web' },
  { id: 'css', label: 'CSS', ext: () => css(), runKind: 'web' },
  { id: 'javascript', label: 'JavaScript', ext: () => javascript({ jsx: false, typescript: false }), runKind: 'web' },
  { id: 'typescript', label: 'TypeScript', ext: () => javascript({ jsx: false, typescript: true }), runKind: 'none' },
  { id: 'jsx', label: 'JSX', ext: () => javascript({ jsx: true, typescript: false }), runKind: 'none' },
  { id: 'python', label: 'Python', ext: () => python(), runKind: 'python' },
  { id: 'json', label: 'JSON', ext: () => json(), runKind: 'json' },
  { id: 'sql', label: 'SQL', ext: () => sql(), runKind: 'none' },
  { id: 'markdown', label: 'Markdown', ext: () => markdown(), runKind: 'none' },
  { id: 'java', label: 'Java', ext: () => java(), runKind: 'none' },
  { id: 'cpp', label: 'C / C++', ext: () => cpp(), runKind: 'none' },
  { id: 'rust', label: 'Rust', ext: () => rust(), runKind: 'none' },
  { id: 'go', label: 'Go', ext: () => go(), runKind: 'none' },
  { id: 'php', label: 'PHP', ext: () => php(), runKind: 'none' },
  { id: 'xml', label: 'XML', ext: () => xml(), runKind: 'none' },
  { id: 'yaml', label: 'YAML', ext: () => yaml(), runKind: 'none' },
  { id: 'vue', label: 'Vue', ext: () => vue(), runKind: 'none' },
  { id: 'sass', label: 'Sass', ext: () => sass(), runKind: 'none' },
  { id: 'less', label: 'Less', ext: () => less(), runKind: 'none' },
  { id: 'liquid', label: 'Liquid', ext: () => liquid(), runKind: 'none' },
  { id: 'wast', label: 'WAT', ext: () => wast(), runKind: 'none' },
  { id: 'ruby', label: 'Ruby', ext: legacyMode(ruby), runKind: 'none' },
  { id: 'shell', label: 'Shell', ext: legacyMode(shell), runKind: 'none' },
  { id: 'dockerfile', label: 'Dockerfile', ext: legacyMode(dockerFile), runKind: 'none' },
  { id: 'csharp', label: 'C#', ext: legacyMode(csharp), runKind: 'none' },
  { id: 'kotlin', label: 'Kotlin', ext: legacyMode(kotlin), runKind: 'none' },
  { id: 'scala', label: 'Scala', ext: legacyMode(scala), runKind: 'none' },
  { id: 'swift', label: 'Swift', ext: legacyMode(swift), runKind: 'none' },
  { id: 'perl', label: 'Perl', ext: legacyMode(perl), runKind: 'none' },
  { id: 'lua', label: 'Lua', ext: legacyMode(lua), runKind: 'none' },
  { id: 'r', label: 'R', ext: legacyMode(rLang), runKind: 'none' },
  { id: 'haskell', label: 'Haskell', ext: legacyMode(haskell), runKind: 'none' },
  { id: 'clojure', label: 'Clojure', ext: legacyMode(clojure), runKind: 'none' },
  { id: 'powershell', label: 'PowerShell', ext: legacyMode(powerShell), runKind: 'none' },
];

function defaultCode() {
  return {
    html: `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Infobloom Playground</title>
  </head>
  <body>
    <h1>Hello, Infobloom</h1>
    <p>Edit HTML/CSS/JS and click Run.</p>
    <button id="btn">Click me</button>
    <div id="out"></div>
  </body>
</html>`,
    css: `body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 16px; }
h1 { color: #2563eb; }
button { padding: 10px 14px; border: none; border-radius: 8px; background: #2563eb; color: white; cursor: pointer; }
button:hover { background: #1d4ed8; }`,
    javascript: `const btn = document.getElementById('btn');
const out = document.getElementById('out');
btn.addEventListener('click', () => {
  out.textContent = 'Button clicked at ' + new Date().toLocaleTimeString();
});`,
    typescript: `type User = { name: string; age: number };
const u: User = { name: "Ada", age: 28 };
console.log(\`\${u.name} is \${u.age}\`);`,
    jsx: `function Welcome({ name }) {
  return <h2>Hello, {name}!</h2>;
}
// JSX needs a build step to run in browser — use editor for syntax practice.`,
    python: `print("Hello from Python!")
for i in range(3):
    print("i =", i)`,
    json: `{
  "app": "LearnWithInfobloom",
  "version": 1,
  "features": ["lessons", "quizzes", "code editor"]
}`,
    sql: `-- Sample query
SELECT id, title, level
FROM courses
WHERE published = true
ORDER BY title
LIMIT 10;`,
    markdown: `# Welcome
**Bold** and *italic* text.

- Item one
- Item two

\`\`\`javascript
console.log("code block");
\`\`\``,
    java: `public class Hello {
    public static void main(String[] args) {
        System.out.println("Hello, Java!");
    }
}`,
    cpp: `#include <iostream>

int main() {
    std::cout << "Hello, C++!" << std::endl;
    return 0;
}`,
    rust: `fn main() {
    println!("Hello, Rust!");
}`,
    go: `package main

import "fmt"

func main() {
    fmt.Println("Hello, Go!")
}`,
    php: `<?php
$name = "Infobloom";
echo "Hello, " . $name . "!";
?>`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<course id="1">
  <title>Introduction</title>
  <lesson order="1">Getting started</lesson>
</course>`,
    yaml: `app: LearnWithInfobloom
version: 1
features:
  - lessons
  - quizzes
  - code_editor`,
    vue: `<template>
  <p>{{ message }}</p>
</template>

<script setup>
import { ref } from "vue";
const message = ref("Hello, Vue!");
</script>`,
    sass: `$primary: #2563eb

body
  font-family: system-ui, sans-serif
  color: $primary`,
    less: `@primary: #2563eb;

body {
  font-family: system-ui, sans-serif;
  color: @primary;
}`,
    liquid: `{% assign course = "Infobloom" %}
<h1>Welcome to {{ course }}</h1>
{% if true %}
  <p>Syntax highlighting for Shopify Liquid.</p>
{% endif %}`,
    wast: `(module
  (func $add (param $a i32) (param $b i32) (result i32)
    local.get $a
    local.get $b
    i32.add)
  (export "add" (func $add)))`,
    ruby: `def greet(name)
  "Hello, #{name}!"
end

puts greet("Ruby")`,
    shell: `#!/usr/bin/env bash
set -euo pipefail
echo "Hello from the shell"
for i in 1 2 3; do
  echo "i=$i"
done`,
    dockerfile: `FROM node:20-alpine
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
EXPOSE 3000
CMD ["npm", "start"]`,
    csharp: `using System;

class Program {
    static void Main() {
        Console.WriteLine("Hello, C#!");
    }
}`,
    kotlin: `fun main() {
    val name = "Kotlin"
    println("Hello, $name!")
}`,
    scala: `object Hello {
  def main(args: Array[String]): Unit = {
    println("Hello, Scala!")
  }
}`,
    swift: `import Foundation

let greeting = "Hello, Swift!"
print(greeting)`,
    perl: `#!/usr/bin/env perl
use strict;
use warnings;

my $name = "Perl";
print "Hello, $name!\\n";`,
    lua: `-- Lua sample
local function greet(who)
  return "Hello, " .. who .. "!"
end

print(greet("Lua"))`,
    r: `# R sample
square <- function(x) {
  x * x
}

vals <- c(1, 2, 3)
print(square(vals))`,
    haskell: `-- Haskell sample
double :: Int -> Int
double x = x + x

main :: IO ()
main = print (double 21)`,
    clojure: `(defn greet [name]
  (str "Hello, " name "!"))

(println (greet "Clojure"))`,
    powershell: `# PowerShell sample
$Name = "PowerShell"
Write-Output "Hello, $Name!"
Get-Date -Format "yyyy-MM-dd"`,
  };
}

const EDITOR_ONLY_HINT =
  'This language runs outside the browser. Use the editor for syntax practice, then run your code locally or in an online compiler (e.g. replit.com, godbolt.org).';

async function ensurePyodide() {
  if (window.__pyodideReady) return window.__pyodideReady;
  window.__pyodideReady = new Promise((resolve, reject) => {
    if (window.loadPyodide) {
      return resolve(window.loadPyodide({ indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/' }));
    }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/pyodide.js';
    s.async = true;
    s.onload = () => {
      if (!window.loadPyodide) reject(new Error('Pyodide failed to load.'));
      else resolve(window.loadPyodide({ indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/' }));
    };
    s.onerror = () => reject(new Error('Could not load Pyodide script.'));
    document.head.appendChild(s);
  });
  return window.__pyodideReady;
}

export default function Playground() {
  const [active, setActive] = useState('html');
  const [code, setCode] = useState(() => defaultCode());
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const iframeRef = useRef(null);

  const activeDef = useMemo(() => LANG_DEFS.find((l) => l.id === active) || LANG_DEFS[0], [active]);
  const showWebPreview = WEB_IDS.has(active);

  const runWeb = () => {
    const srcDoc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>${code.css || ''}</style>
  </head>
  <body>
    ${code.html || ''}
    <script>
      try {
        ${code.javascript || ''}
      } catch (e) {
        const pre = document.createElement('pre');
        pre.style.whiteSpace = 'pre-wrap';
        pre.style.color = '#b91c1c';
        pre.textContent = 'JavaScript error: ' + (e && e.stack ? e.stack : String(e));
        document.body.appendChild(pre);
      }
    </script>
  </body>
</html>`;
    if (iframeRef.current) iframeRef.current.srcdoc = srcDoc;
    setOutput('');
  };

  const runPython = async () => {
    setRunning(true);
    setOutput('');
    try {
      const pyodide = await ensurePyodide();
      let buffer = '';
      pyodide.setStdout({ batched: (s) => { buffer += s; } });
      pyodide.setStderr({ batched: (s) => { buffer += s; } });
      await pyodide.runPythonAsync(code.python || '');
      setOutput(buffer || '(no output)');
    } catch (e) {
      setOutput(String(e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  const runJson = () => {
    const raw = (code.json || '').trim();
    if (!raw) {
      setOutput('Empty JSON.');
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setOutput(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setOutput(`Invalid JSON: ${e.message || e}`);
    }
  };

  const handleRun = () => {
    const kind = activeDef.runKind;
    if (kind === 'web') {
      runWeb();
      return;
    }
    if (kind === 'python') {
      return runPython();
    }
    if (kind === 'json') {
      return runJson();
    }
    setOutput(EDITOR_ONLY_HINT);
  };

  const rightTitle = showWebPreview ? 'Preview' : 'Output';

  return (
    <div className="playground">
      <div className="playground-inner">
        <div className="playground-header">
          <h1 className="playground-title">Code Editor</h1>
          <p className="playground-sub">
            Web preview for HTML, CSS, and JavaScript. Python runs in-browser (Pyodide). Many more languages (YAML, Vue, Sass, Shell, Dockerfile, C#, and others) have syntax highlighting; use{' '}
            <strong>Run</strong> to validate JSON or see notes for compiled languages.
          </p>
        </div>

        <div className="playground-toolbar">
          <div className="playground-tabs" role="tablist" aria-label="Languages">
            {LANG_DEFS.map((l) => (
              <button
                key={l.id}
                type="button"
                className={`playground-tab ${active === l.id ? 'active' : ''}`}
                onClick={() => setActive(l.id)}
                role="tab"
                aria-selected={active === l.id}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button type="button" className="playground-run" onClick={handleRun} disabled={running}>
            {running ? 'Running…' : 'Run'}
          </button>
        </div>

        <div className="playground-grid">
          <section className="playground-panel">
            <div className="playground-panel-header">
              <span className="playground-panel-title">{activeDef.label}</span>
            </div>
            <CodeMirror
              value={code[active] || ''}
              height="420px"
              theme={oneDark}
              extensions={[activeDef.ext()]}
              onChange={(value) => setCode((prev) => ({ ...prev, [active]: value }))}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
              }}
            />
          </section>

          <section className="playground-panel">
            <div className="playground-panel-header">
              <span className="playground-panel-title">{rightTitle}</span>
            </div>
            {showWebPreview ? (
              <>
                <iframe
                  ref={iframeRef}
                  title="Preview"
                  className="playground-iframe"
                  sandbox="allow-scripts allow-forms allow-modals allow-popups"
                />
                <p className="playground-hint">
                  Tip: edit HTML, CSS, and JavaScript tabs, then click <strong>Run</strong>.
                </p>
              </>
            ) : (
              <pre className="playground-output playground-output-full" aria-live="polite">
                {output || (activeDef.runKind === 'python' ? '' : 'Click Run for output or validation.')}
              </pre>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
